import { useEffect, useRef, useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import { useSelector } from 'react-redux'
import { CSSTransition, SwitchTransition } from "react-transition-group"

import { VehicleImage as VehicleImageComponent } from '../../VehicleImage'
import styles from './vehicle-image.module.scss'
import { constants } from '@/lib/constants'
import { selectPaintOptions, selectSelectedVehiclePaintOption } from '@/store/selectors/vehicle'
import { PaintOption, Vehicle, VehicleGalleryItem } from '@/types/vehicle'
import { Button, ButtonHierarchy } from '../../Button'
import { Size, Theme } from '@/types/system'

export type VehicleImageProps = {
  vehicle: Vehicle
  setActiveSection?: (id: string) => void
  activeSection?: string
  setActiveImage: (id: string) => void
  activeImage: string
}

export const VehicleImage = ({ vehicle, setActiveSection, activeSection, setActiveImage, activeImage }: VehicleImageProps) => {
  const selectedPaintOption = useSelector(selectSelectedVehiclePaintOption)
  const paintOptions = useSelector(selectPaintOptions)
  const nodeRef = useRef(null)
  const ref = useRef<HTMLDivElement>(null)

  const [image, setImage] = useState<string>('')
  const [defaultImage, setDefaultImage] = useState(vehicle.gallery?.images.filter((img: VehicleGalleryItem) => img.angle === 'sideview')[0]?.path)
  const [backupImage, setBackupImage] = useState(vehicle.images.side)
  const [frontImage, setFrontImage] = useState(vehicle.gallery?.images.filter((img: VehicleGalleryItem) => img.angle === 'frontview')[0]?.path)
  const [rearImage, setRearImage] = useState(vehicle.gallery?.images.filter((img: VehicleGalleryItem) => img.angle === 'rearview')[0]?.path)

  const onImageClick = () => {
    if (setActiveSection) {
      setActiveSection(constants.configPageSections.GALLERY)
    }
  }

  useEffect(() => {
    if (activeSection === constants.configPageSections.SPECIFICATION) {
      setImage(getImageView(vehicle.gallery?.images, 'sideview'))
      setFrontImage(getImageView(vehicle.gallery?.images, 'frontview'))
      setRearImage(getImageView(vehicle.gallery?.images, 'rearview'))
    }
  }, [activeSection, vehicle])

  const getImageView = (images: VehicleGalleryItem[] | undefined, angle: string) =>
    images?.find((img: VehicleGalleryItem) => img.angle === angle)?.path || ''

  useEffect(() => {
    let img
    let frontImg
    let rearImg
    const altImg = vehicle.images.side

    if (selectedPaintOption && paintOptions.length > 0 && activeSection !== constants.configPageSections.SPECIFICATION) {
      const doesPaintExistOnNewVehicle = paintOptions.filter((color: PaintOption) => color.id === selectedPaintOption.id).length > 0

      if (doesPaintExistOnNewVehicle) {
        img = getImageView(
          paintOptions.find((color: PaintOption) => color.id === selectedPaintOption.id)?.images, 'sideview',
        )
        frontImg = getImageView(
          paintOptions.find((color: PaintOption) => color.id === selectedPaintOption.id)?.images, 'frontview',
        )
        rearImg = getImageView(
          paintOptions.find((color: PaintOption) => color.id === selectedPaintOption.id)?.images, 'rearview',
        )
      } else {
        img = getImageView(vehicle.gallery?.images, 'sideview')
        frontImg = getImageView(vehicle.gallery?.images, 'frontview')
        rearImg = getImageView(vehicle.gallery?.images, 'rearview')
      }
    } else {
      img = getImageView(vehicle.gallery?.images, 'sideview')
      frontImg = getImageView(vehicle.gallery?.images, 'frontview')
      rearImg = getImageView(vehicle.gallery?.images, 'rearview')
    }

    setDefaultImage(img)
    setBackupImage(altImg)
    setImage(img)
    setFrontImage(frontImg)
    setRearImage(rearImg)
  }, [paintOptions, selectedPaintOption, vehicle, activeSection])

  return (
    <>
      <Fade
        className={`vehicle-gallery__container ${styles.container}`}
        triggerOnce
        delay={500}>
        <div
          className={`${styles.wrapper}`}
          data-active-section={activeSection?.toLowerCase()}
          ref={ref}>
          <div
            className={`${styles.carousel}`}
            ref={ref}>
              <SwitchTransition mode="in-out">
                <CSSTransition
                  key={image}
                  classNames={{
                    enter: styles.enter,
                    enterActive: styles['enter--active'],
                    enterDone: styles['enter--done'],
                    exit: styles.exit,
                    exitActive: styles['exit--active'],
                    exitDone: styles['exit--done'],
                  }}
                  timeout={1500}
                  nodeRef={nodeRef}
                >
                  <div
                    className={styles.item}>
                    <div
                      className={styles.content}
                      onClick={onImageClick}>
                      <VehicleImageComponent
                        key={defaultImage}
                        imagePath={image}
                        backupImagePath={backupImage}
                        alt={`${vehicle.manufacturerName} ${vehicle.modelName}`}
                        height={800}
                        width={1200}
                        priority
                        quality={90}
                        type="DEFAULT"
                      />
                    </div>
                  </div>
                </CSSTransition>
              </SwitchTransition>
              {!!frontImage && activeSection === constants.configPageSections.SPECIFICATION && (
                <>
                  <div className={`${styles.item} ${styles.scaled} ${styles.centered}`}>
                    <div className={styles.content}>
                      <VehicleImageComponent
                        key={frontImage}
                        imagePath={frontImage}
                        backupImagePath={''}
                        alt={`${vehicle.manufacturerName} ${vehicle.modelName}`}
                        height={800}
                        width={1200}
                        priority
                        quality={90}
                        type="DEFAULT"
                      />
                    </div>
                  </div>
                  <div className={`${styles.item} ${styles.scaled} ${styles.centered}`}>
                    <div className={styles.content}>
                      <VehicleImageComponent
                        key={rearImage}
                        imagePath={rearImage}
                        backupImagePath={''}
                        alt={`${vehicle.manufacturerName} ${vehicle.modelName}`}
                        height={800}
                        width={1200}
                        priority
                        quality={90}
                        type="DEFAULT"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
        </div>
      </Fade>
      {!!frontImage && activeSection === constants.configPageSections.SPECIFICATION && (
        <div
          className={styles.arrows}
          data-navigation-position={
            activeImage === 'sideview'
              ? 'start'
              :  activeImage === 'rearview'
                ? 'end'
                : 'middle'
          }>
          <Button
            onClick={() => {
              setActiveImage(activeImage === 'sideview' ? 'rearview' : activeImage === 'rearview' ? 'frontview' : 'sideview')
              if (activeImage === 'sideview') {
                ref?.current?.scrollTo({
                  left: 100000,
                  behavior: 'smooth',
                })
              } else {
                ref?.current?.scrollTo({
                  left: ref.current.scrollLeft - ref?.current?.children[0]?.clientWidth,
                  behavior: 'smooth',
                })
              }
            }}
            icon="ChevronLeft"
            size={Size.ExtraSmall}
            hierarchy={ButtonHierarchy.BezeledGray}
            theme={Theme.light}
          />
          <Button
            onClick={() => {
              setActiveImage(activeImage === 'sideview' ? 'frontview' : activeImage === 'frontview' ? 'rearview' : 'sideview')
              if (activeImage === 'rearview') {
                ref?.current?.scrollTo({
                  left: 0,
                  behavior: 'smooth',
                })
              } else {
                ref?.current?.scrollTo({
                  left: ref.current.scrollLeft + ref?.current?.children[0]?.clientWidth,
                  behavior: 'smooth',
                })
              }
            }}
            theme={Theme.light}
            icon="ChevronRight"
            size={Size.ExtraSmall}
            hierarchy={ButtonHierarchy.BezeledGray}
          />
        </div>
      )}
    </>
  )
}
